import { IconName } from '@lyse-as/formds-icons';
import { Component, Event, EventEmitter, h, Prop } from '@stencil/core';

import { FdsInputAutocomplete, FdsInputType, FdsInputVariant } from '@/components/input/input';

/**
 * @slot custom-description - This slot is for adding custom description under text field.
 */
@Component({
  tag: 'fds-text-field',
  styleUrl: 'styles/text-field.scss',
  scoped: true,
})
export class TextField {
  /**
   * The label text of the input
   */
  @Prop() label!: string;

  /**
   * Sets the name attribute for the input
   */
  @Prop({ reflect: true }) name!: string;

  /**
   * Adds a description text underneath the input
   */
  @Prop() description: string;

  /**
   * Sets the placeholder of the input
   */
  @Prop() placeholder: string;

  /**
   * Sets the type of input, i.e. "text", "password", "email" etc
   */
  @Prop() type: FdsInputType;

  /**
   * Sets the value of the input
   */
  @Prop({ mutable: true, reflect: true }) value: string;

  /**
   * Which variant of input to render
   */
  @Prop({ reflect: true }) variant: FdsInputVariant = 'primary';

  /**
   * Sets the autocomplete attribute of the input
   */
  @Prop() autocomplete: FdsInputAutocomplete = 'off';

  /**
   * If true, the input will be disabled and users cannot interact with it
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * Sets the max length of the text inside the textfield
   */
  @Prop() maxLength: number;

  /**
   * Sets the min length of the text inside the textfield
   */
  @Prop() minLength: number;

  /**
   * Sets the max number in the textfield
   */
  @Prop() max: number;

  /**
   * Sets the min number in the textfield
   */
  @Prop() min: number;

  /**
   * If true, will hide the label. It will exists in the DOM, just not be visible
   */
  @Prop() hideLabel = false;

  /**
   * If set, renders an icon to the left hand side of the input text
   */
  @Prop() leftIcon: IconName;

  /**
   * Sets the required attribute of the input
   */
  @Prop({ reflect: true }) required = false;

  /**
   * Sets the readonly attribute of the input
   */
  @Prop({ reflect: true }) readonly = false;

  /**
   * Emitted when the text field value changes
   */
  @Event() fdsChange: EventEmitter<string>;

  private handleChange = (event: CustomEvent<string>) => {
    this.value = event.detail ?? undefined;
    event.stopPropagation();
    this.fdsChange.emit(this.value);
  };

  render() {
    return (
      <fds-input
        label={this.label}
        name={this.name}
        value={this.value}
        type={this.type}
        description={this.description}
        placeholder={this.placeholder}
        leftIcon={this.leftIcon}
        variant={this.variant}
        maxLength={this.maxLength}
        minLength={this.minLength}
        max={this.max}
        min={this.min}
        disabled={this.disabled}
        autocomplete={this.autocomplete}
        hideLabel={this.hideLabel}
        required={this.required}
        readonly={this.readonly}
        onFdsChange={this.handleChange}
      >
        <slot name="custom-description" slot="custom-description" />
      </fds-input>
    );
  }
}
